/** 规则链节点清单（前后端共用；执行器在 dtwin-server） */

export type RuleNodeCategory = 'filter' | 'enrich' | 'transform' | 'action' | 'external' | 'flow' | 'debug';

export interface RuleNodeField {
  key: string;
  label: string;
  type: 'string' | 'number' | 'boolean' | 'select' | 'json' | 'code' | 'secret';
  required?: boolean;
  default?: unknown;
  options?: Array<{ label: string; value: string }>;
  placeholder?: string;
  help?: string;
  example?: string;
  min?: number;
  max?: number;
}

export interface RuleNodeDefinition {
  type: 'filter' | 'transform' | 'enrich' | 'action' | 'external' | 'flow' | 'debug';
  kind: string;
  title: string;
  category: RuleNodeCategory;
  description: string;
  fields: RuleNodeField[];
  defaults: Record<string, unknown>;
  outputs?: string[];
  capabilities?: {
    control?: boolean;
    external?: boolean;
    writes?: boolean;
    multiOutput?: boolean;
    multiMessage?: boolean;
  };
}

export const RULE_NODE_CATEGORIES: Array<{ id: RuleNodeCategory; label: string; color: string }> = [
  { id: 'filter', label: '过滤', color: '#fa8c16' },
  { id: 'enrich', label: '数据补充', color: '#13c2c2' },
  { id: 'transform', label: '转换', color: '#722ed1' },
  { id: 'action', label: '操作', color: '#52c41a' },
  { id: 'external', label: '外部', color: '#2f54eb' },
  { id: 'flow', label: '流程', color: '#eb2f96' },
  { id: 'debug', label: '调试', color: '#8c8c8c' },
];

export const RULE_NODE_DEFINITIONS: RuleNodeDefinition[] = [
  {
    type: 'filter', kind: 'switch', title: 'Switch 分支', category: 'filter',
    description: '按多条规则把消息路由到不同分支；未命中走 otherwise',
    fields: [
      {
        key: 'rules', label: '规则（JSON）', type: 'json', required: true,
        help: '数组，每项 { id, label?, expression }；按顺序求值，命中走 caseN',
        example: '[{"id":"case0","label":"高温","expression":"msg.data.v > 5"}]',
      },
      { key: 'stopOnMatch', label: '命中即停止', type: 'boolean', default: true, help: '关闭时继续匹配后续规则（可能走多条分支）' },
    ],
    defaults: { rules: [{ id: 'case0', label: '条件1', expression: 'msg.data.v > 5' }], stopOnMatch: true },
    outputs: ['case0', 'otherwise'],
    capabilities: { multiOutput: true },
  },
  {
    type: 'filter', kind: 'messageType', title: '消息类型过滤', category: 'filter',
    description: '按 eventCode/消息类型过滤（status/event/alarm 等）',
    fields: [
      { key: 'types', label: '消息类型', type: 'string', required: true, default: 'status,event', help: '逗号分隔；匹配 eventCode 或 msgType', example: 'status,event' },
      { key: 'eventCodes', label: '事件码', type: 'string', help: '可选，逗号分隔；匹配 eventCode', example: 'device:status,device:event' },
    ],
    defaults: { types: 'status,event' },
    outputs: ['true', 'false'],
  },
  {
    type: 'transform', kind: 'change', title: '字段变更', category: 'transform',
    description: '无代码 set/move/delete/rename 字段',
    fields: [
      {
        key: 'operations', label: '操作（JSON）', type: 'json', required: true,
        help: '数组；op=set/move/delete/rename；path 支持 a.b[0]',
        example: '[{"op":"set","path":"data.tempF","valuePath":"data.temp*9/5+32"}]',
      },
    ],
    defaults: { operations: [{ op: 'set', path: 'data.ruleHit', value: true }] },
  },
  {
    type: 'transform', kind: 'jsonPath', title: 'JSON Path 提取', category: 'transform',
    description: '提取 JSON 子结构到指定字段',
    fields: [
      { key: 'path', label: '源路径', type: 'string', required: true, placeholder: 'data.temp', help: '支持 a.b[0]' },
      { key: 'outputKey', label: '输出字段', type: 'string', required: true, default: 'value', placeholder: 'value' },
      { key: 'keepOriginal', label: '保留原字段', type: 'boolean', default: true },
    ],
    defaults: { path: 'data.temp', outputKey: 'value', keepOriginal: true },
  },
  {
    type: 'transform', kind: 'renameKeys', title: '重命名字段', category: 'transform',
    description: '按映射批量重命名/删除字段',
    fields: [
      { key: 'mapping', label: '映射（JSON）', type: 'json', required: true, help: '{"旧字段":"新字段"}；新字段为空表示删除', example: '{"temp":"temperature"}' },
      { key: 'recursive', label: '递归处理', type: 'boolean', default: false },
    ],
    defaults: { mapping: {}, recursive: false },
  },
  {
    type: 'enrich', kind: 'deviceAttributes', title: '设备属性补充', category: 'enrich',
    description: '把设备名称/状态/固件/网络/IO 等属性合并到消息',
    fields: [
      { key: 'keys', label: '属性（可选）', type: 'string', help: '逗号分隔；留空=全部', example: 'deviceName,status,fwVer' },
      { key: 'prefix', label: '输出字段', type: 'string', default: 'deviceAttributes' },
    ],
    defaults: { prefix: 'deviceAttributes' },
  },
  {
    type: 'enrich', kind: 'latestTelemetry', title: '最新遥测补充', category: 'enrich',
    description: '把设备最新点位/IO 合并到消息（进程内缓存，默认 5 分钟）',
    fields: [
      { key: 'keys', label: '点位（可选）', type: 'string', help: '逗号分隔；留空=全部', example: 'temp,ec' },
      { key: 'prefix', label: '输出字段', type: 'string', default: 'latest' },
      { key: 'maxAgeMs', label: '最大时效(ms)', type: 'number', default: 300000, min: 0, max: 86400000 },
    ],
    defaults: { prefix: 'latest', maxAgeMs: 300000 },
  },
  {
    type: 'external', kind: 'httpRequest', title: 'HTTP 请求', category: 'external',
    description: '向外部 HTTP API 发送请求（GET/POST/PUT）',
    fields: [
      { key: 'url', label: 'URL', type: 'string', required: true, placeholder: 'https://...' },
      { key: 'method', label: '方法', type: 'select', default: 'POST', options: [{ label: 'GET', value: 'GET' }, { label: 'POST', value: 'POST' }, { label: 'PUT', value: 'PUT' }] },
      { key: 'headers', label: 'Headers（JSON）', type: 'json', default: {} },
      { key: 'bodyPath', label: 'Body 取值路径', type: 'string', help: '留空=整个消息；GET 忽略', placeholder: 'data' },
      { key: 'timeoutMs', label: '超时(ms)', type: 'number', default: 5000, min: 200, max: 30000 },
    ],
    defaults: { method: 'POST', headers: {}, timeoutMs: 5000 },
    capabilities: { external: true },
  },
  {
    type: 'external', kind: 'mqttPublish', title: 'MQTT 发布', category: 'external',
    description: '向外部 MQTT Broker 发布消息',
    fields: [
      { key: 'brokerUrl', label: 'Broker URL', type: 'string', required: true, placeholder: 'mqtt://host:1883', help: '支持 mqtt:// / mqtts://' },
      { key: 'topic', label: 'Topic', type: 'string', required: true, placeholder: 'dtwince/rule/out' },
      { key: 'qos', label: 'QoS', type: 'select', default: '0', options: [{ label: '0', value: '0' }, { label: '1', value: '1' }] },
      { key: 'retain', label: 'Retain', type: 'boolean', default: false },
      { key: 'payloadPath', label: 'Payload 取值路径', type: 'string', help: '留空=整个消息', placeholder: 'data' },
    ],
    defaults: { brokerUrl: 'mqtt://127.0.0.1:1883', topic: 'dtwince/rule/out', qos: '0', retain: false },
    capabilities: { external: true },
  },
  {
    type: 'flow', kind: 'subchain', title: '子规则链', category: 'flow',
    description: '调用另一条已发布规则链，输入/输出串接',
    fields: [
      { key: 'chainId', label: '目标链 ID', type: 'string', required: true, placeholder: 'uuid' },
      { key: 'versionMode', label: '版本', type: 'select', default: 'current', options: [{ label: '当前发布版本', value: 'current' }, { label: '固定版本', value: 'pinned' }] },
      { key: 'versionId', label: '固定版本 ID', type: 'string', help: 'versionMode=pinned 时使用' },
    ],
    defaults: { versionMode: 'current' },
  },
  {
    type: 'action', kind: 'alarm', title: '告警创建/清除', category: 'action',
    description: '创建或清除一个规则链告警状态（写入输出点位并广播）',
    fields: [
      { key: 'mode', label: '动作', type: 'select', default: 'create', options: [{ label: '创建/触发', value: 'create' }, { label: '清除', value: 'clear' }] },
      { key: 'key', label: '告警 Key', type: 'string', required: true, placeholder: 'line1.tempAlarm' },
      { key: 'severity', label: '级别', type: 'select', default: 'warn', options: [{ label: 'info', value: 'info' }, { label: 'warn', value: 'warn' }, { label: 'error', value: 'error' }] },
      { key: 'message', label: '消息', type: 'string', placeholder: '温度超限' },
    ],
    defaults: { mode: 'create', key: 'rule.alarm', severity: 'warn', message: '规则链告警' },
    capabilities: { writes: true },
  },
  {
    type: 'action', kind: 'delay', title: '延迟', category: 'action',
    description: '延迟指定毫秒后继续（进程内，重启丢失）',
    fields: [
      { key: 'delayMs', label: '延迟(ms)', type: 'number', required: true, default: 1000, min: 0, max: 30000 },
    ],
    defaults: { delayMs: 1000 },
  },
  {
    type: 'transform', kind: 'splitArray', title: '数组拆分', category: 'transform',
    description: '把数组拆成多条消息，逐条执行下游节点',
    fields: [
      { key: 'path', label: '数组路径', type: 'string', required: true, placeholder: 'data.items' },
      { key: 'itemKey', label: '元素字段名', type: 'string', default: 'item', help: '每条消息写入 {itemKey: 元素}' },
      { key: 'maxItems', label: '最大条数', type: 'number', default: 100, min: 1, max: 500 },
    ],
    defaults: { path: 'data.items', itemKey: 'item', maxItems: 100 },
    capabilities: { multiMessage: true },
  },
  {
    type: 'filter', kind: 'deviceAttribute', title: '设备属性过滤', category: 'filter',
    description: '按设备属性/在线状态过滤',
    fields: [
      { key: 'key', label: '属性', type: 'string', required: true, placeholder: 'status' },
      { key: 'op', label: '比较', type: 'select', default: 'eq', options: [{ label: '等于', value: 'eq' }, { label: '不等于', value: 'ne' }, { label: '大于', value: 'gt' }, { label: '小于', value: 'lt' }, { label: '包含', value: 'contains' }] },
      { key: 'value', label: '值', type: 'string', placeholder: 'online' },
    ],
    defaults: { key: 'status', op: 'eq', value: 'online' },
    outputs: ['true', 'false'],
  },
  {
    type: 'enrich', kind: 'deviceDetails', title: '设备详情补充', category: 'enrich',
    description: '补充设备详情（名称/状态/固件/网络/IO）',
    fields: [{ key: 'prefix', label: '输出字段', type: 'string', default: 'deviceDetails' }],
    defaults: { prefix: 'deviceDetails' },
  },
  {
    type: 'enrich', kind: 'tenantDetails', title: '租户详情补充', category: 'enrich',
    description: '补充当前租户 ID/名称',
    fields: [{ key: 'prefix', label: '输出字段', type: 'string', default: 'tenantDetails' }],
    defaults: { prefix: 'tenantDetails' },
  },
  {
    type: 'action', kind: 'saveAttributes', title: '保存属性', category: 'action',
    description: '把值保存为规则链属性输出（key=attribute.{name}）',
    fields: [
      { key: 'key', label: '属性名', type: 'string', required: true, placeholder: 'line1.state' },
      { key: 'valuePath', label: '取值路径', type: 'string', placeholder: 'data.state' },
    ],
    defaults: { key: 'rule.attribute' },
    capabilities: { writes: true },
  },
  {
    type: 'external', kind: 'email', title: '邮件通知', category: 'external',
    description: '发送通知邮件（MAIL_ENABLED=true 时实际发送）',
    fields: [
      { key: 'to', label: '收件人', type: 'string', required: true, help: '逗号分隔' },
      { key: 'subject', label: '主题', type: 'string', required: true },
      { key: 'message', label: '正文', type: 'string' },
    ],
    defaults: { subject: 'DTwince 规则链通知', message: '规则链触发' },
    capabilities: { external: true },
  },
  {
    type: 'flow', kind: 'checkpoint', title: '检查点', category: 'flow',
    description: '记录运行检查点（当前为透传标记，不阻塞）',
    fields: [{ key: 'name', label: '名称', type: 'string', default: 'checkpoint' }],
    defaults: { name: 'checkpoint' },
  },
  {
    type: 'flow', kind: 'ack', title: '确认', category: 'flow',
    description: '确认消息（当前为透传标记，不阻塞）',
    fields: [{ key: 'name', label: '名称', type: 'string', default: 'ack' }],
    defaults: { name: 'ack' },
  },
  {
    type: 'flow', kind: 'join', title: '汇合', category: 'flow',
    description: '把上游多条消息汇合成一条（joined 数组）',
    fields: [{ key: 'name', label: '名称', type: 'string', default: 'join' }],
    defaults: { name: 'join' },
    capabilities: { multiMessage: true },
  },
  {
    type: 'action', kind: 'rpc', title: 'RPC 控制', category: 'action',
    description: '向设备下发 RTU 命令，可等待设备回复；用于控制 IO',
    fields: [
      {
        key: 'method', label: '命令', type: 'select', required: true,
        options: [
          { label: 'write_do（写 DO）', value: 'write_do' },
          { label: 'do_set（设置 DO）', value: 'do_set' },
          { label: 'set_ma（4-20mA）', value: 'set_ma' },
          { label: 'set_dac（DAC）', value: 'set_dac' },
          { label: 'get_status（状态）', value: 'get_status' },
          { label: 'reboot（重启）', value: 'reboot' },
        ],
      },
      { key: 'params', label: '参数（JSON）', type: 'json', help: '如 {"channel":"rl1","value":1}', example: '{"channel":"rl1","value":1}' },
      { key: 'waitReply', label: '等待设备回复', type: 'boolean', default: true },
      { key: 'timeoutMs', label: '回复超时(ms)', type: 'number', default: 5000, min: 200, max: 30000 },
    ],
    defaults: { method: 'get_status', params: {}, waitReply: true, timeoutMs: 5000 },
    capabilities: { control: true },
  },
  {
    type: 'action', kind: 'saveTelemetry', title: '保存遥测', category: 'action',
    description: '把消息中的值保存为规则链输出点位（供大屏/3D 绑定）',
    fields: [
      { key: 'key', label: '输出 Key', type: 'string', required: true, placeholder: 'line1.temp', help: '租户内唯一；场景/大屏按此 key 绑定' },
      { key: 'valuePath', label: '取值路径', type: 'string', placeholder: 'data.temp', help: '留空=整个消息' },
      { key: 'ttlSec', label: 'TTL(秒)', type: 'number', default: 0, min: 0, help: '0=不过期' },
    ],
    defaults: { key: 'rule.value', valuePath: 'data.temp', ttlSec: 0 },
    capabilities: { writes: true },
  },
  {
    type: 'flow', kind: 'output', title: '规则链输出', category: 'flow',
    description: '输出点位/事件；3D 场景与大屏按 key 绑定',
    fields: [
      { key: 'key', label: '输出 Key', type: 'string', required: true, placeholder: 'line1.alarm', help: '租户内唯一；场景/大屏按此 key 绑定' },
      { key: 'outputType', label: '输出类型', type: 'select', default: 'point', options: [{ label: '点位（可持久化）', value: 'point' }, { label: '事件（WS）', value: 'event' }] },
      { key: 'valuePath', label: '取值路径', type: 'string', placeholder: 'data.temp', help: '留空=整个消息' },
      { key: 'ttlSec', label: 'TTL(秒)', type: 'number', default: 0, min: 0 },
    ],
    defaults: { key: 'rule.output', outputType: 'point', valuePath: 'data.temp', ttlSec: 0 },
    capabilities: { writes: true },
  },
];

const DEFAULT_KIND: Record<string, string> = {
  filter: 'script',
  enrich: 'device',
  transform: 'script',
  action: 'alert',
  external: 'httpRequest',
  flow: 'output',
  debug: 'debug',
};

export function resolveRuleNodeKind(node: { type?: string; kind?: string }): string {
  return String(node.kind || DEFAULT_KIND[String(node.type || '')] || '');
}

export function getRuleNodeDefinition(type: string, kind?: string): RuleNodeDefinition | undefined {
  const k = kind || DEFAULT_KIND[type];
  return RULE_NODE_DEFINITIONS.find((d) => d.type === type && d.kind === k);
}

export function getRuleNodeOutputs(node: { type?: string; kind?: string; config?: Record<string, unknown> }): string[] {
  const def = getRuleNodeDefinition(String(node.type || ''), node.kind);
  if (def?.kind === 'switch') {
    const rules = Array.isArray(node.config?.rules) ? (node.config!.rules as unknown[]) : [];
    return [...rules.map((_, i) => `case${i}`), 'otherwise'];
  }
  return def?.outputs || ['out'];
}
